// Builds + dispatches `com.ping.file` through MLS (encrypted blob on S3).
//
// Mirrors `sendImageMessage.ts` exactly: encrypt the raw file bytes with
// `mediaCrypto`, upload the ciphertext via the multipart flow
// (`mediaUpload`) to obtain a `media_id`, wrap that into a `blob_ref`,
// then `conv.send` a `com.ping.file` envelope. The receiver-side decoder
// (shared `packages/ui/src/appEvent/envelope.ts::decodeFile`) reads
// `{ blob_ref, name, size, mime, page_count? }` — we emit the same shape
// with `pages` as the optional page-count alias the doc-spec FileBody
// promotes via `decodeFile`'s `p.page_count ?? p.pageCount` read.
//
//   { kind: 'com.ping.file',
//     payload: {
//       blob_ref: { media_id, content_key, content_iv, hash },
//       name:  'report.pdf',
//       mime:  'application/pdf',
//       size:  12345,
//       pages?: 4,            // optional (PDF page count)
//     } }

import {
  commitSentMessage as sharedCommitSentMessage,
  encodeEnvelopeJsonBytes,
  encodeEnvelopeJsonString,
  lastMessagePreview,
  markMessageFailed as sharedMarkMessageFailed,
  mintEventId,
  persistSentMessage as sharedPersistSentMessage,
} from '@ping/ui';

import { log } from '@core/log/log';
import { retrieveIdentity } from '@core/persistence/identity';

import { blobRefToWire, type BlobRefWire,encryptMediaBlob } from './mediaCrypto';
import { uploadCiphertextMultipart } from './mediaUpload';
import { webConversationsPersister, webMessagesPersister } from './persistenceAdapters';

interface SentMessageEnvelope {
  conversation_id: Uint8Array;
  epoch: number | bigint;
  content_hash: Uint8Array;
  hlc: { wall_ms: number | bigint; logical: number };
}

interface SendableConversation {
  send(plaintext: Uint8Array): Promise<SentMessageEnvelope>;
}

export interface FileEnvelopePayload {
  readonly blob_ref: BlobRefWire;
  readonly name: string;
  readonly mime: string;
  readonly size: number;
  /** Optional page count (e.g. PDF). Maps to doc-spec FileBody.page_count. */
  readonly pages?: number | undefined;
  /** Optional caption from the tray preview overlay. */
  readonly caption?: string | undefined;
}

export interface FileEnvelope {
  readonly kind: 'com.ping.file';
  readonly payload: FileEnvelopePayload;
  /** Thread root content-hash when sent as a reply into a thread. */
  readonly parent_id?: string;
}

export interface SendFileMessageOptions {
  readonly conv: SendableConversation;
  readonly conversationId: string;
  readonly file: File;
  readonly pages?: number | undefined;
  readonly signal?: AbortSignal | undefined;
  /**
   * Precomputed local-echo row id. When supplied the helper upserts the
   * SAME pending row (pending → sent) instead of inserting a fresh one,
   * keeping the on-screen bubble stable while the blob uploads.
   */
  readonly localId?: string | undefined;
  /** Caption sent alongside the file (from the tray preview overlay). */
  readonly caption?: string | undefined;
  /** Live upload progress (0..1) — forwarded to `uploadCiphertextMultipart`. */
  readonly onProgress?: ((fraction: number) => void) | undefined;
  /** When set, posts the file as a reply into this thread root. */
  readonly parentId?: string | undefined;
}

export interface SendFileMessageResult {
  readonly contentHashHex: string;
  readonly messageId: string;
}

export async function sendFileMessage(opts: SendFileMessageOptions): Promise<SendFileMessageResult> {
  const { conv, conversationId, file, pages, signal, localId, caption, onProgress, parentId } = opts;

  const identity = await retrieveIdentity();

  const buffer = await file.arrayBuffer();
  const plaintext = new Uint8Array(buffer);
  const mime = file.type !== '' ? file.type : 'application/octet-stream';

  const { ciphertext, secrets, hash } = await encryptMediaBlob(plaintext);
  let mediaId: string;
  try {
    ({ mediaId } = await uploadCiphertextMultipart(ciphertext, mime, signal, onProgress));
  } catch (err) {
    if (localId !== undefined) {
      await sharedMarkMessageFailed({ messages: webMessagesPersister, id: localId });
    }
    throw err;
  }

  const blobRef = blobRefToWire(mediaId, secrets, hash);
  const payload: FileEnvelopePayload = {
    blob_ref: blobRef,
    name: file.name,
    mime,
    size: file.size,
    ...(typeof pages === 'number' ? { pages } : {}),
    ...(caption !== undefined && caption.length > 0 ? { caption } : {}),
  };
  const eventId = localId ?? mintEventId();
  const encodable = {
    kind: 'com.ping.file' as const,
    event_id: eventId,
    // Thread reply → self-identify with `payload.thread: true` so the main
    // timeline folds it out (parent_id alone also marks an inline reply-quote).
    // For non-text kinds parent_id only ever means a thread reply.
    payload: { ...payload, ...(parentId !== undefined ? { thread: true } : {}) },
    ...(parentId !== undefined ? { parent_id: parentId } : {}),
  };
  const payloadBytes = encodeEnvelopeJsonBytes(encodable);
  const payloadBody = encodeEnvelopeJsonString(encodable);

  let sentEnvelope: SentMessageEnvelope;
  try {
    sentEnvelope = await conv.send(payloadBytes);
  } catch (err) {
    if (localId !== undefined) {
      await sharedMarkMessageFailed({ messages: webMessagesPersister, id: localId });
    }
    throw err;
  }
  const contentHashHex = bytesToHex(sentEnvelope.content_hash);

  const sentAt = Date.now();
  const epoch =
    typeof sentEnvelope.epoch === 'bigint' ? Number(sentEnvelope.epoch) : sentEnvelope.epoch;

  // Local-echo commit — patch the SAME pending row in place when the
  // caller pre-wrote one; otherwise legacy single-shot insert.
  if (localId !== undefined) {
    await webMessagesPersister.update(localId, { body: payloadBody });
    if (identity) {
      const preview = lastMessagePreview(payloadBody);
      await sharedCommitSentMessage({
        messages: webMessagesPersister,
        conversations: webConversationsPersister,
        id: localId,
        conversationId,
        contentHash: contentHashHex,
        epoch,
        serverSeq: sentAt,
        sentAt,
        senderAccountId: identity.accountId,
        ...(preview !== undefined ? { preview } : {}),
      });
    }
    log.debug({
      event: 'file.send.ok',
      conversationIdPrefix: conversationId.slice(0, 6),
      mediaIdPrefix: mediaId.slice(0, 8),
      sizeBytes: file.size,
    });
    return { contentHashHex, messageId: localId };
  }

  const messageId = eventId;

  if (identity) {
    await sharedPersistSentMessage({
      messages: webMessagesPersister,
      conversations: webConversationsPersister,
      record: {
        id: messageId,
        conversationId,
        epoch,
        serverSeq: sentAt,
        contentHash: contentHashHex,
        senderAccountId: identity.accountId,
        body: payloadBody,
        sentAt,
        deliveryState: 'sent',
        ...(parentId !== undefined
          ? { inReplyTo: { messageId: parentId, preview: 'File' } }
          : {}),
      },
    });
  } else {
    log.warn({ event: 'file.persistSent.no_identity' });
  }

  log.debug({
    event: 'file.send.ok',
    conversationIdPrefix: conversationId.slice(0, 6),
    mediaIdPrefix: mediaId.slice(0, 8),
    sizeBytes: file.size,
  });

  return { contentHashHex, messageId };
}

/** Detects whether a persisted `Message.body` is a file envelope. */
export function isFileEnvelopeBody(body: string | undefined): boolean {
  if (body === undefined || body.length === 0) return false;
  if (!body.startsWith('{')) return false;
  return body.includes('"com.ping.file"');
}

/**
 * Parses a `com.ping.file` envelope from a persisted message body.
 * Returns `undefined` on any decode failure so the caller can fall
 * back to the plain-text bubble.
 */
export function parseFileEnvelope(body: string | undefined): FileEnvelopePayload | undefined {
  if (body === undefined) return undefined;
  try {
    const parsed = JSON.parse(body) as Partial<FileEnvelope>;
    if (parsed.kind !== 'com.ping.file') return undefined;
    const p = parsed.payload;
    const ref = p?.blob_ref;
    if (
      p === undefined ||
      ref === undefined ||
      typeof ref.media_id !== 'string' ||
      typeof ref.content_key !== 'string' ||
      typeof ref.content_iv !== 'string' ||
      typeof ref.hash !== 'string' ||
      typeof p.name !== 'string' ||
      typeof p.mime !== 'string' ||
      typeof p.size !== 'number'
    ) {
      return undefined;
    }
    return {
      blob_ref: ref,
      name: p.name,
      mime: p.mime,
      size: p.size,
      ...(typeof p.pages === 'number' ? { pages: p.pages } : {}),
      ...(typeof p.caption === 'string' ? { caption: p.caption } : {}),
    };
  } catch {
    return undefined;
  }
}

function bytesToHex(b: Uint8Array): string {
  let out = '';
  for (let i = 0; i < b.length; i++) {
    out += (b[i] ?? 0).toString(16).padStart(2, '0');
  }
  return out;
}
